import { useEffect, useRef, useState } from "react";
import { api, formatApiError, UPLOADS_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Upload, X, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Galerie d'images externes jointes à une fiche reprise ou intervention.
// Tant que le document n'est pas clôturé, on peut en ajouter et en
// supprimer librement ; une fois clôturé, elles restent consultables
// (agrandissement au clic) mais plus jamais modifiables.
//
// Bug corrigé : sur un document PAS ENCORE créé (itemId absent), il fallait
// obligatoirement enregistrer le formulaire avant de pouvoir ajouter la
// moindre photo (les routes d'upload nécessitent un identifiant de document
// réel). Ce composant fonctionne désormais aussi en "mode brouillon" :
// - `itemId` absent -> les photos choisies sont mises en ATTENTE localement
//   (aperçu immédiat, jamais encore envoyées au serveur) ; le composant
//   parent doit alors fournir `stagedFiles`/`onStagedFilesChange` pour
//   piloter cette liste, et les envoyer lui-même juste après la création
//   réelle du document (voir RepriseFormPage.jsx/InterventionFormPage.jsx :
//   persist() boucle sur ces fichiers en attente dès que l'identifiant
//   définitif est connu).
// - `itemId` présent -> fonctionnement normal, upload immédiat vers le
//   serveur comme avant.
export function DocumentImagesGallery({ module, itemId, isCloturee, canEdit, stagedFiles, onStagedFilesChange, section, title }) {
  const isDraft = !itemId;
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(!isDraft);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const collection = module === "reprise" ? "reprises" : "interventions";

  const load = async () => {
    try {
      const { data } = await api.get(`/${collection}/${itemId}/images`, { params: section ? { section } : undefined });
      setImages(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (itemId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, section]);

  // Bug corrigé : les aperçus des photos en attente (mode brouillon)
  // appelaient `URL.createObjectURL(file)` directement dans le rendu — donc
  // à CHAQUE nouveau rendu du formulaire (par ex. en tapant dans un autre
  // champ), une NOUVELLE URL locale était créée pour la même image, sans
  // jamais révoquer les précédentes. Résultat observé : l'aperçu affichait
  // une icône d'image cassée. Les URL sont désormais créées UNE SEULE FOIS
  // par fichier (à son ajout), conservées tant que le fichier reste en
  // attente, et explicitement révoquées quand il est retiré ou remplacé —
  // seul un vrai changement de la liste des fichiers en régénère.
  const [stagedPreviews, setStagedPreviews] = useState([]);
  useEffect(() => {
    if (!isDraft) return;
    const urls = (stagedFiles || []).map((file) => URL.createObjectURL(file));
    setStagedPreviews(urls);
    return () => { urls.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft, stagedFiles]);

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (isDraft) {
      // Mode brouillon : mise en attente locale uniquement, rien n'est
      // envoyé au serveur tant que le document n'existe pas réellement.
      onStagedFilesChange([...(stagedFiles || []), file]);
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (section) fd.append("section", section);
    try {
      await api.post(`/${collection}/${itemId}/images`, fd);
      toast.success("Image ajoutée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await api.delete(`/${collection}/${itemId}/images/${imageId}`);
      toast.success("Image supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeStaged = (index) => {
    const next = [...(stagedFiles || [])];
    next.splice(index, 1);
    onStagedFilesChange(next);
  };

  if (loading) return null;
  const displayCount = isDraft ? (stagedFiles || []).length : images.length;
  if (displayCount === 0 && (!canEdit || isCloturee)) return null;

  const testSuffix = section ? `-${section}` : "";

  return (
    <div className="mt-4" data-testid={`document-images-gallery${testSuffix}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold flex items-center gap-1.5">
          {title || "Images jointes"}
          {isCloturee && <Lock size={12} className="text-muted-foreground" />}
        </p>
        {canEdit && !isCloturee && (
          <>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChosen} data-testid={`document-images-file-input${testSuffix}`} />
            <Button
              type="button" size="sm" variant="outline" className="gap-1.5"
              onClick={() => fileInputRef.current?.click()} disabled={uploading}
              data-testid={`document-images-add-button${testSuffix}`}
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              Ajouter une image
            </Button>
          </>
        )}
      </div>

      {isDraft && displayCount > 0 && (
        <p className="text-xs text-muted-foreground mb-2">
          En attente d'enregistrement — ces photos seront envoyées automatiquement dès que vous enregistrerez la fiche.
        </p>
      )}

      {displayCount === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune image jointe.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {isDraft
            ? (stagedFiles || []).map((file, idx) => (
                <div key={idx} className="relative group" data-testid={`document-image-staged-${idx}`}>
                  <div className="w-full aspect-square rounded-lg overflow-hidden border border-dashed border-border bg-secondary">
                    {stagedPreviews[idx] && <img src={stagedPreviews[idx]} alt={file.name} className="w-full h-full object-cover" />}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStaged(idx)}
                    className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Retirer cette image"
                    data-testid={`document-image-staged-remove-${idx}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))
            : images.map((img) => (
                <div key={img.id} className="relative group" data-testid={`document-image-${img.id}`}>
                  <button
                    type="button"
                    onClick={() => setPreview(img)}
                    className="w-full aspect-square rounded-lg overflow-hidden border border-border bg-secondary"
                  >
                    <img src={`${UPLOADS_BASE}${img.url}`} alt={img.original_name || "Image jointe"} className="w-full h-full object-cover" />
                  </button>
                  {!isCloturee && canEdit && (
                    <button
                      type="button"
                      onClick={() => handleDelete(img.id)}
                      className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Supprimer cette image"
                      data-testid={`document-image-delete-${img.id}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
        </div>
      )}

      {isCloturee && images.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Document clôturé : ces images sont consultables mais ne peuvent plus être ajoutées ni supprimées.
        </p>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="document-image-preview-overlay"
        >
          <button
            type="button" onClick={() => setPreview(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X size={28} />
          </button>
          <img
            src={`${UPLOADS_BASE}${preview.url}`}
            alt={preview.original_name || "Image jointe"}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
